import {
  Building2,
  CircleAlert,
  ContactRound,
  Database,
  FileText,
  ShieldCheck,
  X,
} from 'lucide-react'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type FormEvent,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import type { Organization } from '../../types/crm'
import {
  createProspect,
  getProspectOptions,
  ProspectApiError,
  type CreatedProspect,
} from './prospectClient'
import {
  buildProspectReview,
  EMPTY_PROSPECT_FORM,
  validateProspectForm,
  type ProspectFieldErrors,
  type ProspectFieldKey,
  type ProspectFormValues,
  type ProspectOptions,
  type ProspectReviewRow,
} from './prospectForm'

type AddNewProspectModalProps = {
  organizations: Organization[]
  onClose: () => void
  onCreated: (prospect: CreatedProspect) => void
}

type OptionsState = 'loading' | 'ready' | 'error'
type ProspectStep = 'form' | 'review'

const CLOSE_MS = 180

const FIELD_ACCESSIBLE_NAMES: Record<ProspectFieldKey, string> = {
  companyName: 'Company name',
  ownerName: 'Owner / opportunity name',
  priority: 'Priority',
  website: 'Website',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  email: 'Email',
  phone: 'Phone',
  city: 'City',
  state: 'State',
  source: 'Source',
  opportunityValue: 'Opportunity value',
  notes: 'Notes',
}

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function SectionHeading({ icon, number, title, description }: { icon: ReactNode; number: number; title: string; description: string }) {
  return (
    <div className="add-prospect-section-heading">
      <span aria-hidden="true">{icon}</span>
      <div><small>Section {number}</small><h3>{title}</h3><p>{description}</p></div>
    </div>
  )
}

function InlineError({ field, errors }: { field: ProspectFieldKey; errors: ProspectFieldErrors }) {
  return errors[field]
    ? <small className="add-prospect-inline-error" id={`add-prospect-${field}-error`}>{errors[field]}</small>
    : null
}

function ReviewValue({ row }: { row: ProspectReviewRow }) {
  if (row.value === null || row.value === '') return <span className="add-prospect-blank">Blank</span>
  if (typeof row.value === 'number') {
    return <>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(row.value)}</>
  }
  return <>{row.value}</>
}

function ReviewRows({ label, rows }: { label: string; rows: ProspectReviewRow[] }) {
  return (
    <section className="add-prospect-review-card" aria-label={label}>
      <header><Database aria-hidden="true" size={17} /><strong>{label}</strong></header>
      <dl>
        {rows.map((row) => <div key={row.label}><dt>{row.label}</dt><dd><ReviewValue row={row} /></dd></div>)}
      </dl>
    </section>
  )
}

export function AddNewProspectModal({ organizations, onClose, onCreated }: AddNewProspectModalProps) {
  const [step, setStep] = useState<ProspectStep>('form')
  const [values, setValues] = useState<ProspectFormValues>(() => ({ ...EMPTY_PROSPECT_FORM }))
  const [errors, setErrors] = useState<ProspectFieldErrors>({})
  const [formError, setFormError] = useState('')
  const [options, setOptions] = useState<ProspectOptions | null>(null)
  const [optionsState, setOptionsState] = useState<OptionsState>('loading')
  const [optionsError, setOptionsError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [announcement, setAnnouncement] = useState('Loading live CRM options…')
  const [isClosing, setIsClosing] = useState(false)
  const modalRef = useRef<HTMLElement>(null)
  const firstFieldRef = useRef<HTMLInputElement>(null)
  const reviewHeadingRef = useRef<HTMLHeadingElement>(null)
  const retryRef = useRef<HTMLButtonElement>(null)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const closeTimerRef = useRef<number | null>(null)
  const optionsControllerRef = useRef<AbortController | null>(null)
  const submitControllerRef = useRef<AbortController | null>(null)
  const submittingRef = useRef(false)
  const closingRef = useRef(false)
  const initialFieldFocusedRef = useRef(false)

  const scheduleClose = useCallback(() => {
    if (closingRef.current) return
    closingRef.current = true
    setIsClosing(true)
    closeTimerRef.current = window.setTimeout(onClose, prefersReducedMotion() ? 0 : CLOSE_MS)
  }, [onClose])

  const requestClose = useCallback(() => {
    if (submittingRef.current) return
    scheduleClose()
  }, [scheduleClose])

  const loadOptions = useCallback(() => {
    optionsControllerRef.current?.abort()
    const controller = new AbortController()
    optionsControllerRef.current = controller
    setOptionsState('loading')
    setOptionsError('')
    setAnnouncement('Loading live CRM options…')

    getProspectOptions(controller.signal)
      .then((nextOptions) => {
        if (controller.signal.aborted) return
        setOptions(nextOptions)
        setOptionsState('ready')
        setAnnouncement(nextOptions.writeEnabled
          ? 'Live CRM options loaded.'
          : 'Live CRM options loaded. Prospect creation is not enabled on this deployment.')
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return
        const message = cause instanceof ProspectApiError
          ? cause.message
          : 'The live CRM options could not be loaded. Try again.'
        setOptions(null)
        setOptionsState('error')
        setOptionsError(message)
        setAnnouncement(message)
      })
  }, [])

  useEffect(() => {
    loadOptions()
    return () => optionsControllerRef.current?.abort()
  }, [loadOptions])

  useEffect(() => {
    const appShell = document.querySelector<HTMLElement>('.app-shell')
    const previousOverflow = document.body.style.overflow
    const previousScrollbarGutter = document.documentElement.style.scrollbarGutter
    appShell?.classList.add('is-lead-modal-open')
    document.body.style.overflow = 'hidden'
    document.documentElement.style.scrollbarGutter = 'stable'
    closeButtonRef.current?.focus()

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault()
        requestClose()
        return
      }
      if (event.key !== 'Tab' || !modalRef.current) return
      const focusable = Array.from(modalRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [href], [tabindex]:not([tabindex="-1"])',
      )).filter((element) => element.getAttribute('aria-hidden') !== 'true')
      if (focusable.length === 0) return
      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => {
      appShell?.classList.remove('is-lead-modal-open')
      document.body.style.overflow = previousOverflow
      document.documentElement.style.scrollbarGutter = previousScrollbarGutter
      window.removeEventListener('keydown', handleKeyDown)
      optionsControllerRef.current?.abort()
      submitControllerRef.current?.abort()
      if (closeTimerRef.current !== null) window.clearTimeout(closeTimerRef.current)
    }
  }, [requestClose])

  useEffect(() => {
    let frame = 0
    if (optionsState === 'ready' && step === 'form' && !initialFieldFocusedRef.current) {
      initialFieldFocusedRef.current = true
      frame = window.requestAnimationFrame(() => {
        const activeElement = document.activeElement
        if (activeElement === document.body || activeElement === closeButtonRef.current) firstFieldRef.current?.focus()
      })
    } else if (optionsState === 'error') {
      frame = window.requestAnimationFrame(() => retryRef.current?.focus())
    } else if (step === 'review') {
      frame = window.requestAnimationFrame(() => reviewHeadingRef.current?.focus())
    }
    return () => {
      if (frame) window.cancelAnimationFrame(frame)
    }
  }, [optionsState, step])

  const review = useMemo(
    () => options ? buildProspectReview(values, options, options.organizations ?? organizations) : null,
    [options, organizations, values],
  )

  function updateField(field: ProspectFieldKey, event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const value = event.target.value
    setValues((current) => ({ ...current, [field]: value }))
    setErrors((current) => {
      const next = { ...current, [field]: undefined }
      if (field === 'companyName' || field === 'ownerName') {
        next.companyName = undefined
        next.ownerName = undefined
      }
      return next
    })
    setFormError('')
  }

  function reviewProspect(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (!options) return
    const nextErrors = validateProspectForm(values, options)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) {
      const message = 'Correct the highlighted fields before reviewing this prospect.'
      setFormError(message)
      setAnnouncement(message)
      const firstError = Object.keys(nextErrors)[0] as ProspectFieldKey
      window.requestAnimationFrame(() => document.getElementById(`add-prospect-${firstError}`)?.focus())
      return
    }
    setFormError('')
    setAnnouncement('Review the exact Google Sheets values before adding this prospect.')
    setStep('review')
  }

  function backToForm() {
    if (submittingRef.current) return
    setFormError('')
    setStep('form')
  }

  async function addProspect() {
    if (!review || !options?.writeEnabled || submittingRef.current) return
    submittingRef.current = true
    setSubmitting(true)
    setFormError('')
    setAnnouncement('Adding prospect…')
    const controller = new AbortController()
    submitControllerRef.current = controller
    let createdProspect: CreatedProspect

    try {
      createdProspect = await createProspect(review.submission, controller.signal)
    } catch (cause) {
      if (controller.signal.aborted) return
      submittingRef.current = false
      setSubmitting(false)
      const error = cause instanceof ProspectApiError ? cause : null
      const message = error?.message ?? 'The prospect could not be added. Nothing was written. Try again.'
      setFormError(message)
      setAnnouncement(message)
      if (error && Object.keys(error.fieldErrors).length > 0) {
        setErrors(error.fieldErrors)
        setStep('form')
        const firstError = Object.keys(error.fieldErrors)[0] as ProspectFieldKey
        window.requestAnimationFrame(() => document.getElementById(`add-prospect-${firstError}`)?.focus())
      }
      return
    } finally {
      if (submitControllerRef.current === controller) submitControllerRef.current = null
    }

    submittingRef.current = false
    setSubmitting(false)
    setAnnouncement('Prospect added to Google Sheets')
    scheduleClose()
    onCreated(createdProspect)
  }

  const fieldProps = (field: ProspectFieldKey) => ({
    'aria-describedby': errors[field] ? `add-prospect-${field}-error` : undefined,
    'aria-invalid': Boolean(errors[field]),
    'aria-label': FIELD_ACCESSIBLE_NAMES[field],
    id: `add-prospect-${field}`,
    name: field,
    onChange: (event: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => updateField(field, event),
    value: values[field],
  })

  return createPortal(
    <div className={`lead-modal-layer add-prospect-layer${isClosing ? ' is-closing' : ''}`} data-testid="add-prospect-modal-layer">
      <button aria-label="Close Add New Prospect" className="lead-modal-backdrop" disabled={submitting} onClick={requestClose} type="button" />
      <section
        aria-busy={submitting}
        aria-labelledby="add-prospect-title"
        aria-modal="true"
        className="lead-modal add-prospect-modal"
        ref={modalRef}
        role="dialog"
      >
        <header className="add-prospect-header">
          <div><span>Quick action</span><h2 id="add-prospect-title">Add New Prospect</h2><p>Create linked records in the live Wolf Forge CRM.</p></div>
          <button aria-label="Close Add New Prospect" className="icon-button lead-modal-close" disabled={submitting} onClick={requestClose} ref={closeButtonRef} title="Close" type="button"><X size={21} /></button>
        </header>

        <div className="lead-modal-content add-prospect-content">
          <p aria-live="polite" className="sr-only" role="status">{announcement}</p>

          {optionsState === 'loading' ? (
            <div className="add-prospect-options-state" role="status"><span className="add-prospect-loading-line" /><strong>Loading live CRM options…</strong><p>Priority, source, and state are read directly from Google Sheets.</p></div>
          ) : null}

          {optionsState === 'error' ? (
            <div className="add-prospect-options-state is-error" role="alert"><CircleAlert aria-hidden="true" size={25} /><strong>Live options are unavailable</strong><p>{optionsError}</p><button className="button button-secondary" onClick={loadOptions} ref={retryRef} type="button">Try again</button></div>
          ) : null}

          {optionsState === 'ready' && options && step === 'form' ? (
            <form className="add-prospect-form" noValidate onSubmit={reviewProspect}>
              {!options.writeEnabled ? <div className="add-prospect-write-warning" role="status"><CircleAlert aria-hidden="true" size={18} /><span><strong>Prospect creation is not enabled.</strong> You can review the form, but the protected final action remains unavailable.</span></div> : null}

              <section>
                <SectionHeading icon={<Building2 size={19} />} number={1} title="Prospect identity" description="At least a company or owner name is required." />
                <div className="add-prospect-field-grid">
                  <label className="add-prospect-field" htmlFor="add-prospect-companyName"><span>Company name</span><input autoComplete="organization" className="text-input" maxLength={200} placeholder="Company or organization" ref={firstFieldRef} type="text" {...fieldProps('companyName')} /><InlineError field="companyName" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-ownerName"><span>Owner / opportunity name</span><input autoComplete="name" className="text-input" maxLength={200} placeholder="Prospect owner" type="text" {...fieldProps('ownerName')} /><InlineError field="ownerName" errors={errors} /></label>
                  <label className="add-prospect-field is-wide" htmlFor="add-prospect-website"><span>Website</span><input autoComplete="url" className="text-input" inputMode="url" maxLength={2048} placeholder="example.com" type="url" {...fieldProps('website')} /><InlineError field="website" errors={errors} /></label>
                </div>
              </section>

              <section>
                <SectionHeading icon={<ContactRound size={19} />} number={2} title="Contact information" description="Optional details are written only when real values are supplied." />
                <div className="add-prospect-field-grid">
                  <label className="add-prospect-field" htmlFor="add-prospect-email"><span>Email</span><input autoComplete="email" className="text-input" inputMode="email" maxLength={254} placeholder="owner@example.com" type="email" {...fieldProps('email')} /><InlineError field="email" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-phone"><span>Phone</span><input autoComplete="tel" className="text-input" inputMode="tel" maxLength={50} placeholder="(555) 123-4567" type="tel" {...fieldProps('phone')} /><InlineError field="phone" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-facebook"><span>Facebook</span><input autoComplete="url" className="text-input" inputMode="url" maxLength={2048} placeholder="facebook.com/company" type="url" {...fieldProps('facebook')} /><InlineError field="facebook" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-linkedin"><span>LinkedIn</span><input autoComplete="url" className="text-input" inputMode="url" maxLength={2048} placeholder="linkedin.com/company/example" type="url" {...fieldProps('linkedin')} /><InlineError field="linkedin" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-city"><span>City</span><input autoComplete="address-level2" className="text-input" maxLength={100} type="text" {...fieldProps('city')} /><InlineError field="city" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-state"><span>State</span><select autoComplete="address-level1" className="text-input" {...fieldProps('state')}><option value="">No state selected</option>{options.state.map((state) => <option key={state} value={state}>{state}</option>)}</select><InlineError field="state" errors={errors} /></label>
                </div>
              </section>

              <section>
                <SectionHeading icon={<Database size={19} />} number={3} title="CRM information" description="Priority and source use current Dropdowns values." />
                <div className="add-prospect-field-grid">
                  <label className="add-prospect-field" htmlFor="add-prospect-priority"><span>Priority <i>Required</i></span><select className="text-input" required {...fieldProps('priority')}><option value="">Select priority</option>{options.priority.map((priority) => <option key={priority} value={priority}>{priority}</option>)}</select><InlineError field="priority" errors={errors} /></label>
                  <label className="add-prospect-field" htmlFor="add-prospect-source"><span>Source <i>Required</i></span><select className="text-input" required {...fieldProps('source')}><option value="">Select source</option>{options.source.map((source) => <option key={source} value={source}>{source}</option>)}</select><InlineError field="source" errors={errors} /></label>
                  <label className="add-prospect-field is-wide" htmlFor="add-prospect-opportunityValue"><span>Opportunity value</span><div className="add-prospect-money-field"><span aria-hidden="true">$</span><input className="text-input" inputMode="decimal" placeholder="Leave blank when unknown" type="text" {...fieldProps('opportunityValue')} /></div><InlineError field="opportunityValue" errors={errors} /></label>
                </div>
              </section>

              <section>
                <SectionHeading icon={<FileText size={19} />} number={4} title="Notes" description="Notes are copied into both linked records for V1 compatibility." />
                <label className="add-prospect-field" htmlFor="add-prospect-notes"><span>Notes</span><textarea className="text-input" maxLength={5000} placeholder="Add real context about this prospect" rows={5} {...fieldProps('notes')} /><InlineError field="notes" errors={errors} /></label>
              </section>

              {formError ? <p className="add-prospect-form-error" role="alert">{formError}</p> : null}
              <div className="add-prospect-actions"><button className="button button-primary" type="submit">Review prospect</button></div>
            </form>
          ) : null}

          {optionsState === 'ready' && options && step === 'review' && review ? (
            <section className="add-prospect-review" aria-labelledby="add-prospect-review-title">
              <div className="add-prospect-review-heading"><span>Final review</span><h3 id="add-prospect-review-title" ref={reviewHeadingRef} tabIndex={-1}>Confirm the Google Sheets records</h3><p>The server will revalidate live options and duplicates immediately before the atomic write.</p></div>

              {review.existingOrganization ? (
                <div className="add-prospect-reuse"><Building2 aria-hidden="true" size={19} /><div><strong>Reuse existing organization: {review.organizationName}</strong><p>Organizations row {review.existingOrganization.sheetRow} keeps its exact Sheet spelling. No Organizations row will be added or changed; only the Opportunity below will be created.</p></div></div>
              ) : review.organizationRows ? <ReviewRows label="Organizations · B:M" rows={review.organizationRows} /> : null}

              <ReviewRows label="Opportunities · B:M" rows={review.opportunityRows} />

              {!options.writeEnabled ? <div className="add-prospect-write-warning" role="status"><CircleAlert aria-hidden="true" size={18} /><span><strong>Final submission is unavailable.</strong> Enable protected Sheet writes on the server before adding this prospect.</span></div> : null}
              {formError ? <p className="add-prospect-form-error" role="alert">{formError}</p> : null}
              <div className="add-prospect-actions"><button className="button button-secondary" disabled={submitting} onClick={backToForm} type="button">Back</button><button className="button button-primary" disabled={submitting || !options.writeEnabled} onClick={addProspect} type="button">{submitting ? 'Adding prospect…' : 'Add prospect'}</button></div>
            </section>
          ) : null}
        </div>

        <footer className="lead-modal-footer add-prospect-footer"><ShieldCheck aria-hidden="true" size={14} /> Protected Google Sheets write · No delete action</footer>
      </section>
    </div>,
    document.body,
  )
}
